import { useState, useRef } from 'react';
import { LandingNavbar, LandingFooter } from '@/components/landing';
import { useCal } from '@/hooks/useCal';
import { 
  Zap, 
  Search,
  TrendingUp, 
  AlertTriangle,
  ArrowDownRight,
  Activity,
  Package,
  Clock,
  LayoutGrid,
  AlertOctagon,
  Truck,
  RefreshCw,
  Filter,
  Download,
  MoreHorizontal,
  BarChart3,
  PieChart
} from 'lucide-react';

// --- ReflowAI Design System (Strict Blue/Sapphire Theme) ---
const COLORS = {
  brilliance: '#FCFDFD',      // Main Background
  springtime: '#EDEFF3',      // Glass Card Background
  windweaver: '#C6D1D7',      // Borders
  thistle: '#9FA0B5',         // Accents
  sapphire: '#2F7EDA',        // Primary Brand Color
  blackwater: '#555663',      // Body Text
  darkText: '#0F172A',        // Headings
  danger: '#FF5F57',          // Error/OOS
  warning: '#FEBC2E',         // Low Stock
  success: '#28C840',         // In Stock
};

// --- Global Styles ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      --font-primary: 'Plus Jakarta Sans', sans-serif;
    }

    body {
      font-family: var(--font-primary);
      color: ${COLORS.blackwater};
      background-color: ${COLORS.brilliance};
      overflow-x: hidden;
    }

    /* Halo Gradient Background */
    .halo-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      pointer-events: none;
      overflow: hidden;
    }

    .halo-gradient {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
    }

    /* Glass Utilities */
    .glass-pill {
      background: rgba(252, 253, 253, 0.6); 
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 10px 40px -10px rgba(47, 126, 218, 0.1);
    }
    
    .glass-card {
      background: linear-gradient(145deg, rgba(255,255,255,0.7) 0%, rgba(237,239,243,0.5) 100%);
      backdrop-filter: blur(24px);
      border: 1px solid ${COLORS.windweaver};
      transition: box-shadow 0.2s ease, border-color 0.2s ease;
    }

    .glass-card:hover {
      background: linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(237,239,243,0.8) 100%);
      border-color: ${COLORS.sapphire};
      box-shadow: 0 16px 32px -16px rgba(47, 126, 218, 0.2);
    }

    /* Grid Pattern for Matrix */
    .bg-grid {
      background-size: 40px 40px;
      background-image: linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px);
    }
    
    /* Custom Scrollbar */
    .custom-scrollbar::-webkit-scrollbar {
      width: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(0,0,0,0.05);
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(0,0,0,0.2);
      border-radius: 10px;
    }
  `}</style>
);

// --- Components ---
interface KPICardProps{
  title: string;
  value: string;
  trend: 'up' | 'down';
  trendValue: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const KPICard = ({ title, value, trend, trendValue, icon: Icon, color }: KPICardProps) => (
  <div className="glass-card p-6 rounded-[24px] relative overflow-hidden group">
    <div className="flex justify-between items-start mb-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${color}15`, color: color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className={`flex items-center text-xs font-bold px-2 py-1 rounded-full ${trend === 'up' ? 'text-[#16A34A] bg-green-50' : 'text-[#DC2626] bg-red-50'}`}>
        {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
        {trendValue}
      </div>
    </div>
    <div className="relative z-10">
      <h3 className="text-[#6B7280] text-xs font-bold uppercase tracking-wider mb-1">{title}</h3>
      <p className="text-3xl font-bold text-[#0F172A]">{value}</p>
    </div>
    <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-1/4 translate-y-1/4 transition-opacity duration-200 group-hover:opacity-20" style={{ color: color }}>
      <Icon className="w-32 h-32" />
    </div>
  </div>
);

// --- Store Heatmap Visualizer ---
const StoreMatrix = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Real locations for major metros
  const locations = [
    // Bangalore
    "Koramangala 4th Block", "Indiranagar 100ft Rd", "HSR Layout Sec 1", "Whitefield Main Rd", 
    "Jayanagar 4th Block", "JP Nagar Phase 2", "Bellandur Outer Ring", "Electronic City Ph1",
    // Mumbai
    "Bandra West (Linking Rd)", "Lower Parel (Phoenix)", "Powai (Hiranandani)", "Andheri East (MIDC)", 
    "Malad West (Infinity)", "Colaba Causeway", "Juhu Tara Road", "Ghatkopar East",
    // Delhi NCR
    "DLF Cyber City", "Sector 29 Gurgaon", "Greater Kailash 1", "Vasant Vihar", 
    "Noida Sec 18", "Saket Select City", "Dwarka Sec 10", "Connaught Place",
    // Hyderabad
    "Jubilee Hills Rd 36", "Gachibowli", "Hitech City", "Madhapur", "Banjara Hills Rd 12"
  ];

  const platforms = ['Blinkit', 'Zepto', 'Instamart'];

  // Generate realistic node data
  const stores = useRef(Array.from({ length: 48 }).map((_, i) => {
    const statusRand = Math.random();
    let status = 'good';
    if (statusRand > 0.85) status = 'oos'; // 15% OOS
    else if (statusRand > 0.70) status = 'low'; // 15% Low

    return {
      id: i,
      location: locations[i % locations.length],
      platform: platforms[i % platforms.length],
      status,
      units: status === 'good' ? Math.floor(Math.random() * 50 + 20) : status === 'low' ? Math.floor(Math.random() * 5 + 1) : 0,
      oosTime: status === 'oos' ? `${Math.floor(Math.random() * 4) + 1}h ${Math.floor(Math.random() * 59)}m` : null
    };
  })).current;

  return (
    <div className="glass-card rounded-[24px] md:rounded-[32px] p-4 sm:p-6 md:p-8 shadow-lg h-full overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-3 md:gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-[#2F7EDA]" /> Dark Store Matrix
          </h3>
          <p className="text-xs md:text-sm text-[#6B7280]">Real-time availability across 48 hyperlocal nodes</p>
        </div>
        <div className="flex gap-1 sm:gap-2 bg-[#EDEFF3] p-1 rounded-full flex-wrap">
          {['all', 'blinkit', 'zepto', 'instamart'].map(filter => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold transition-colors duration-150 flex items-center gap-1 sm:gap-2 ${activeFilter === filter ? 'bg-white text-[#2F7EDA] shadow-sm' : 'text-[#6B7280] hover:text-[#555663]'}`}
            >
              {filter !== 'all' && (
                <img 
                  src={filter === 'blinkit' ? '/blinkit_logo.png' : filter === 'zepto' ? '/zepto_logo.png' : '/swiggy_instamart_logo.png'} 
                  alt={filter} 
                  className="w-4 h-4 object-contain" 
                />
              )}
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-2.5 max-h-[350px] sm:max-h-[500px] overflow-y-auto custom-scrollbar pr-1 sm:pr-2">
        {stores.map((store, i) => (
          <div 
            key={i} 
            className={`
              group relative p-2.5 rounded-xl border transition-shadow duration-150 cursor-pointer hover:shadow-md
              ${activeFilter !== 'all' && store.platform.toLowerCase() !== activeFilter ? 'hidden' : 'block'}
              ${store.status === 'good' ? 'bg-white border-[#EDEFF3] hover:border-[#28C840]' : 
                store.status === 'low' ? 'bg-[#FFFBEB] border-[#FEBC2E] hover:border-[#D97706]' : 
                'bg-[#FEF2F2] border-[#FF5F57] hover:border-[#DC2626]'}
            `}
          >
            {/* Header: Platform + Status Dot */}
            <div className="flex justify-between items-center mb-1.5">
               <div className="flex items-center gap-1">
                 <img 
                   src={store.platform === 'Blinkit' ? '/blinkit_logo.png' : store.platform === 'Zepto' ? '/zepto_logo.png' : '/swiggy_instamart_logo.png'} 
                   alt={store.platform} 
                   className="w-3 h-3 object-contain" 
                 />
                 <span className="text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">{store.platform}</span>
               </div>
               <div className={`w-2 h-2 rounded-full shadow-sm ${store.status === 'good' ? 'bg-[#28C840]' : store.status === 'low' ? 'bg-[#FEBC2E]' : 'bg-[#FF5F57] ring-2 ring-[#FF5F57]/25'}`}></div>
            </div>
            
            {/* Location Text */}
            <div className="text-[11px] font-bold text-[#0F172A] leading-tight mb-1.5 truncate" title={store.location}>
              {store.location}
            </div>

            {/* Mini Detail */}
             <div className={`text-[10px] font-semibold flex items-center gap-1 ${store.status === 'good' ? 'text-[#555663]' : store.status === 'low' ? 'text-[#D97706]' : 'text-[#DC2626]'}`}>
               {store.status === 'good' ? <span className="opacity-70">{store.units} units</span> : 
                store.status === 'low' ? <span>Only {store.units} left</span> : 
                <span>OOS: {store.oosTime}</span>}
             </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 sm:gap-6 mt-4 sm:mt-6 justify-center sm:justify-end border-t border-[#EDEFF3] pt-3 sm:pt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
          <span className="text-xs font-bold text-[#555663]">Healthy</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FEBC2E]"></div>
          <span className="text-xs font-bold text-[#555663]">Replenish Soon</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57] ring-2 ring-[#FF5F57]/25"></div>
          <span className="text-xs font-bold text-[#555663]">Revenue Bleed (OOS)</span>
        </div>
      </div>
    </div>
  );
};

// --- Restock Action Table ---
const RestockActions = () => {
  const actions = [
    { sku: 'Oat Milk Barista 1L', location: 'Indiranagar (Blinkit)', risk: '₹4,200', urgency: 'Critical', units: 120, time: '2h 14m' },
    { sku: 'Hazelnut Butter 200g', location: 'Bandra West (Zepto)', risk: '₹2,800', urgency: 'High', units: 45, time: '45m' },
    { sku: 'Protein Bar Choco', location: 'Cyber Hub (Instamart)', risk: '₹1,500', urgency: 'Medium', units: 200, time: '5h 00m' },
    { sku: 'Almond Milk Unsweet', location: 'Gachibowli (Zepto)', risk: '₹1,200', urgency: 'Medium', units: 60, time: '1h 10m' },
    { sku: 'Greek Yogurt 100g', location: 'Powai (Blinkit)', risk: '₹980', urgency: 'Low', units: 150, time: '30m' },
  ];

  return (
    <div className="glass-card rounded-[24px] md:rounded-[32px] p-4 sm:p-6 md:p-8 shadow-lg h-full flex flex-col">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h3 className="text-lg md:text-xl font-bold text-[#0F172A] flex items-center gap-2">
          <AlertOctagon className="w-5 h-5 text-[#FF5F57]" /> Restock War Room
        </h3>
        <button className="text-xs font-bold text-[#2F7EDA] flex items-center gap-1 hover:underline">
          CSV <Download className="w-3 h-3" />
        </button>
      </div>

      <div className="flex-grow space-y-3 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
        {actions.map((item, i) => (
          <div key={i} className="flex items-center justify-between p-4 rounded-2xl bg-[#EDEFF3]/50 border border-[#EDEFF3] hover:bg-white hover:border-[#C6D1D7] transition-colors duration-150 group cursor-pointer">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl bg-white border border-[#EDEFF3] flex items-center justify-center font-bold text-sm shadow-sm transition-colors ${item.urgency === 'Critical' ? 'text-[#FF5F57] group-hover:bg-[#FF5F57] group-hover:text-white' : 'text-[#555663] group-hover:bg-[#2F7EDA] group-hover:text-white'}`}>
                {i + 1}
              </div>
              <div>
                <div className="font-bold text-[#0F172A] text-sm">{item.sku}</div>
                <div className="text-xs text-[#6B7280]">{item.location}</div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm font-bold text-[#FF5F57]">{item.risk}</div>
              <div className="text-[10px] font-medium text-[#555663] mt-0.5">OOS: {item.time}</div>
            </div>
          </div>
        ))}
      </div>
       <button className="mt-4 w-full py-3 rounded-xl border border-dashed border-[#C6D1D7] text-[#6B7280] font-bold text-xs hover:border-[#2F7EDA] hover:text-[#2F7EDA] transition-colors">
          View All 14 Alerts
       </button>
    </div>
  );
};

// --- Platform Breakdown ---
const PlatformBreakdown = () => {
  const data = [
    { name: 'Blinkit', score: 92, color: '#F8CB46', total: 450, oos: 36 },
    { name: 'Zepto', score: 78, color: '#5A2E98', total: 380, oos: 84 },
    { name: 'Instamart', score: 85, color: '#FC8019', total: 410, oos: 61 },
  ];

  return (
    <div className="glass-card rounded-[24px] md:rounded-[32px] p-4 sm:p-6 md:p-8 shadow-lg">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h3 className="text-base md:text-lg font-bold text-[#0F172A] flex items-center gap-2">
          <PieChart className="w-5 h-5 text-[#2F7EDA]" /> Platform Health
        </h3>
        <span className="text-xs font-bold text-[#6B7280] bg-[#EDEFF3] px-2 py-1 rounded-md">Last 24h</span>
      </div>

      <div className="space-y-6">
        {data.map((p, i) => (
          <div key={i}>
            <div className="flex justify-between items-end mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-[#EDEFF3] p-1 flex items-center justify-center">
                  <img 
                    src={p.name === 'Blinkit' ? '/blinkit_logo.png' : p.name === 'Zepto' ? '/zepto_logo.png' : '/swiggy_instamart_logo.png'} 
                    alt={p.name} 
                    className="w-full h-full object-contain" 
                  />
                </div>
                <span className="text-sm font-bold text-[#0F172A]">{p.name}</span>
                <span className={`text-[10px] font-bold px-1.5 rounded ${p.score > 90 ? 'bg-green-100 text-green-700' : p.score > 80 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                  {p.score}%
                </span>
              </div>
              <span className="text-xs text-[#6B7280] font-medium">{p.oos} Stores OOS</span>
            </div>
            <div className="h-3 w-full bg-[#EDEFF3] rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-[width] duration-300" 
                style={{ width: `${p.score}%`, backgroundColor: p.color }}
              ></div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-6 border-t border-[#EDEFF3] flex items-center gap-4">
         <div className="bg-[#EDEFF3] p-3 rounded-xl">
            <Zap className="w-5 h-5 text-[#2F7EDA]" />
         </div>
         <div className="text-xs text-[#555663] leading-tight">
            <span className="font-bold text-[#0F172A]">Insight:</span> Zepto availability dropped 12% in North Delhi region due to weekend surge.
         </div>
      </div>
    </div>
  );
};

// --- Top Offending Products ---
const TopOffenders = () => {
  const products = [
    { name: 'Oat Milk Barista 1L', loss: '₹12,400', location: 'Multiple (8)', trend: 'up', image: '/Oat Milk/71j9ofLt7BL._AC_UF350,350_QL80_.jpg' },
    { name: 'Oat Milk Original 1L', loss: '₹8,200', location: 'Indiranagar', trend: 'down', image: '/Oat Milk/7130yhOMdqL.jpg' },
    { name: 'Oat Milk Unsweetened', loss: '₹6,150', location: 'Koramangala', trend: 'up', image: '/Oat Milk/4_6d04c3f3-a936-4e29-b53a-25523e0f5216.webp' },
    { name: 'Oat Milk Chocolate', loss: '₹4,800', location: 'Cyber City', trend: 'up', image: '/Oat Milk/30791036-6583-414e-b646-9714e5764494_8ZO1AMOYID_MN_16122025.avif' },
  ];

  return (
    <div className="glass-card rounded-[24px] md:rounded-[32px] p-4 sm:p-6 md:p-8 shadow-lg">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h3 className="text-base md:text-lg font-bold text-[#0F172A] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#FF5F57]" /> Revenue Bleeders
        </h3>
        <button 
          className="p-1 rounded-full hover:bg-[#EDEFF3] transition-colors"
          aria-label="More options"
        >
          <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
        </button>
      </div>

      <div className="space-y-4">
        {products.map((p, i) => (
          <div key={i} className="flex items-center justify-between group hover:bg-white/50 p-3 rounded-xl transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white border border-[#EDEFF3] flex items-center justify-center overflow-hidden group-hover:border-[#FF5F57] transition-colors">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-sm font-bold text-[#0F172A]">{p.name}</div>
                <div className="text-[10px] text-[#6B7280]">{p.location}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-[#FF5F57]">{p.loss}</div>
              <div className={`text-[10px] font-bold flex items-center justify-end gap-0.5 ${p.trend === 'up' ? 'text-[#DC2626]' : 'text-green-500'}`}>
                {p.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                Risk
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// --- CTA Component ---
const AvailabilityCTA = () => {
  const { openCalModal } = useCal();

  return (
    <section className="py-20 px-4 text-center">
      <div className="max-w-4xl mx-auto glass-card rounded-[32px] md:rounded-[60px] p-8 md:p-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FF5F57] to-[#FEBC2E]"></div>
        
        <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mb-6 md:mb-8">Never Lose Sales to Stockouts Again</h2>
        <p className="text-base md:text-lg text-[#555663] mb-8 md:mb-10 max-w-2xl mx-auto">Get real-time availability tracking across all dark stores and instant OOS alerts.</p>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-colors duration-150 text-sm tracking-wide bg-[#2F7EDA] text-white hover:opacity-90"
            style={{ boxShadow: '0 10px 30px -5px rgba(47, 126, 218, 0.4)' }}
          >
            Schedule a Call
          </button>
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-colors duration-150 text-sm tracking-wide bg-white/50 text-[#555663] border border-[#C6D1D7] hover:opacity-90"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            Book a Demo
          </button>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
export function AvailabilityLandingPage() {
  const [isLoading, setIsLoading] = useState(false);

  const handleSync = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  return (
    <>
      <GlobalStyles />
      <div className="min-h-screen relative pb-20">
        
        {/* Background Elements */}
        <div className="halo-container">
          <div className="halo-gradient w-[600px] h-[600px] bg-[#2F7EDA] top-[-10%] right-[-10%] opacity-20"></div>
          <div className="halo-gradient w-[500px] h-[500px] bg-[#FF5F57] bottom-[-10%] left-[-10%] opacity-10"></div>
        </div>

        <LandingNavbar />

        <main>
          {/* Hero Section */}
          <section className="pt-36 sm:pt-40 px-4 mb-8">
          <div className="max-w-7xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-bold uppercase tracking-wider mb-6">
              <Activity className="w-3 h-3" /> Live Inventory Intelligence
            </div>
            
            {/* Headline Row */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6 mb-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight">
                Stop Losing Sales to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5F57] to-[#ff8f89]">Empty Shelves</span>
              </h1>
              
              {/* Buttons - Aligned Right */}
              <div className="flex gap-3 flex-shrink-0">
                <button className="bg-white border border-[#C6D1D7] text-[#0F172A] px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-bold text-xs sm:text-sm hover:bg-[#EDEFF3] transition-colors flex items-center gap-2">
                  <Filter className="w-4 h-4" /> Filter Views
                </button>
                <button 
                  onClick={handleSync}
                  disabled={isLoading}
                  className="bg-[#0F172A] text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-bold text-xs sm:text-sm hover:bg-black transition-colors flex items-center gap-2 shadow-xl shadow-slate-200 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> 
                  {isLoading ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            </div>
            
            {/* Subhead - Full Width */}
            <p className="text-base md:text-lg text-[#555663] max-w-4xl mb-8 md:mb-12">
              Track stock in real-time. Detect OOS events instantly and replenish before you lose rank.
            </p>

            {/* KPI Cards */}
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 transition-opacity duration-500 ${isLoading ? 'opacity-50 animate-pulse' : 'opacity-100'}`}>
              <KPICard 
                title="Network Availability" 
                value="84.2%" 
                trend="down" 
                trendValue="2.1%" 
                icon={Package} 
                color="#2F7EDA"
              />
              <KPICard 
                title="Revenue At Risk (Daily)" 
                value="₹1.4 Lac" 
                trend="down" 
                trendValue="12% vs yest" 
                icon={AlertTriangle} 
                color="#FF5F57"
              />
              <KPICard 
                title="Ghost Listings" 
                value="18 SKUs" 
                trend="up" 
                trendValue="4 Fixed" 
                icon={AlertOctagon} 
                color="#FEBC2E"
              />
              <KPICard 
                title="Replenishment Velocity" 
                value="4.2 Hrs" 
                trend="up" 
                trendValue="Faster" 
                icon={Truck} 
                color="#28C840"
              />
            </div>

            {/* Main Visuals Area */}
            <div className={`grid lg:grid-cols-3 gap-8 mb-8 transition-opacity duration-500 ${isLoading ? 'opacity-50 animate-pulse' : 'opacity-100'}`}>
              <div className="lg:col-span-2">
                <StoreMatrix />
              </div>
              <div className="lg:col-span-1 h-full">
                <RestockActions />
              </div>
            </div>

            {/* Bottom Analytics Row */}
            <div className={`grid md:grid-cols-2 gap-8 mb-12 transition-opacity duration-500 ${isLoading ? 'opacity-50 animate-pulse' : 'opacity-100'}`}>
               <PlatformBreakdown />
               <TopOffenders />
            </div>

          </div>
        </section>

        {/* Deep Dive / Explanation Section */}
        <section className="py-20 px-4">
            <div className="max-w-7xl mx-auto glass-pill rounded-[24px] md:rounded-[48px] p-6 md:p-12 bg-white/40">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">Why Availability Matters</h2>
              <p className="text-[#555663] max-w-2xl mx-auto">
                Quick Commerce algorithms penalize OOS heavily. If you're out of stock for more than 4 hours, your organic ranking drops by an average of 40%.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { 
                  icon: Search, 
                  title: "Ghost Listings", 
                  desc: "Sometimes stock exists, but the listing is 'Broken' or 'Unmapped'. We detect these technical errors instantly." 
                },
                { 
                  icon: Clock, 
                  title: "Lost Demand", 
                  desc: "We estimate how much you WOULD have sold based on your SKU's velocity in that specific pincode." 
                },
                { 
                  icon: TrendingUp, 
                  title: "Smart Restock", 
                  desc: "Don't just refill everything. Prioritize the high-velocity dark stores where you are bleeding the most revenue." 
                }
              ].map((feature, i) => (
                <div key={i} className="text-center p-6 rounded-3xl hover:bg-white/50 transition-colors">
                  <div className="w-16 h-16 rounded-2xl bg-[#EDEFF3] flex items-center justify-center mx-auto mb-6 text-[#2F7EDA]">
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">{feature.title}</h3>
                  <p className="text-[#555663] leading-relaxed text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        </main>

      </div>

      <AvailabilityCTA />

      <LandingFooter />
    </>
  );
}
